import { load } from 'cheerio';
import { escapeUnsafe } from '../../platform/Html';
import { FormattingOptionItem } from './FormattingOptionItem';
import { listToEditorForm, listToSavedForm } from './Lists';

// eslint-disable-next-line max-len
const UrlRegex = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/gi;
const MentionRegex = /\[@(.*?)\]\(mention:\/\/to\/(.*?)\/from\/(.*?)\/(.*?)\)/g;

const isValidBulletListLine = (ln: string) => {
    const listRegex = /^\s*\*\s/;
    return listRegex.test(ln);
};

const isValidNumberListLine = (ln: string, prev: string[]) => {
    const listRegex = /^\s*\d+\.\s/;

    // Basically we are checking here that the list as a whole starts with "1.", and each item matches the regex
    if (listRegex.test(ln)) {
        if (ln.startsWith('1.')) return true;
        // Walk backwards and look for the 1.
        for (let i = prev.length - 1; i >= 0; i--) {
            const p = prev[i];
            if (!listRegex.test(p)) return false;
            if (p.startsWith('1.')) return true;
        }
    }

    return false;
};

export const Fauxdown = {

    /**
     * Takes a string in the "editor form", e.g. HTML from Quill, and turns it into "saved form", e.g. our flavour of markdown
     *
     * @param editorForm
     */
    toSavedForm(editorForm: string, formatOpts = FormattingOptionItem.defaults) {
        if (!editorForm?.trim()) return '';

        const $ = load(editorForm);

        $('p:contains("<br>")').each((_, element) => {
            const contents = $(element).html();
            if (contents === '<br>') {
                $(element).replaceWith('\n');
            }
        });

        if (formatOpts.includes('bold')) {
            $('strong').each((_, element) => {
                if ($(element).find('em').length) {
                    $(element).replaceWith(`___${$(element).text()}___`);
                }
                else {
                    $(element).replaceWith(`__${$(element).text()}__`);
                }
            });
        }

        if (formatOpts.includes('italic')) {
            $('em').each((_, element) => {
                if ($(element).find('strong').length) {
                    $(element).replaceWith(`___${$(element).text()}___`);
                }
                else {
                    $(element).replaceWith(`_${$(element).text()}_`);
                }
            });
        }

        if (formatOpts.includes('header')) {
            $('h1, h2, h3, h4, h5, h6').each((_, element) => {
                const size = parseInt(element.tagName.slice(1));
                const content = $(element).html() || '';
                $(element).replaceWith(`${'#'.repeat(size)} ${content}\n`);
            });

        }

        $('p').each((_, element) => {
            const content = $(element).html() || '';
            $(element).replaceWith(`${content}\n`);
        });

        if (formatOpts.includes('mention')) {
            $('span.mention').each((_, element) => {
                const fromId = $(element).attr('data-from-id');
                const toId = $(element).attr('data-to-id');
                const value = $(element).attr('data-value');
                const tag = $(element).attr('data-tag');
                $(element).replaceWith(`[@${value}](mention://to/${toId}/from/${fromId}/${tag || ''})`);
            });
        }

        if (formatOpts.includes('list')) {
            $('ol').each((_, element) => {
                listToSavedForm($, element);
            });
        }

        if (editorForm.match(/<(p|ol|ul|h[1-6])>/)) {
            return $.text().slice(0, -1);
        }

        return $.text();
    },

    /**
     * Takes a string in the "saved form", e.g. our flavour of markdown, and turns it into "editor form", e.g. HTML for Quill
     *
     * @param savedForm
     */
    toEditorForm(savedForm: string, formatOpts = FormattingOptionItem.defaults) {

        // First, escape any HTML that's in there
        savedForm = escapeUnsafe(savedForm);

        const pmd = savedForm.split('\n').map((ln, i, arr) => {
            const prev = arr.slice(0, i);

            if (formatOpts.includes('list')) {
                if (isValidBulletListLine(ln)) {
                    return ln;
                }
                if (isValidNumberListLine(ln, prev)) {
                    return ln;
                }
            }

            if (formatOpts.includes('header')) {
                if (ln.startsWith('###### ')) {
                    return `<h6>${ln.slice(7)}</h6>`;
                }
                if (ln.startsWith('##### ')) {
                    return `<h5>${ln.slice(6)}</h5>`;
                }
                if (ln.startsWith('#### ')) {
                    return `<h4>${ln.slice(5)}</h4>`;
                }
                if (ln.startsWith('### ')) {
                    return `<h3>${ln.slice(4)}</h3>`;
                }
                if (ln.startsWith('## ')) {
                    return `<h2>${ln.slice(3)}</h2>`;
                }
                if (ln.startsWith('# ')) {
                    return `<h1>${ln.slice(2)}</h1>`;
                }
            }

            return `<p>${ln === '' ? '<br>' : ln}</p>`;
        });

        const temp = [];
        const lmd = [];
        pmd.forEach((line, i) => {
            if (!line.match(/^<\w+>/)) {
                temp.push(pmd[i]);
            }
            else {
                if (temp.length > 0) {
                    lmd.push(listToEditorForm(temp));
                    temp.length = 0;
                }
                lmd.push(pmd[i]);
            }
        });

        if (temp.length > 0) {
            lmd.push(listToEditorForm(temp));
        }

        // Remove all Urls before we process inlines
        const urls = [] as string[];

        const urlsRemoved = lmd.join('').replace(UrlRegex, (match) => {
            urls.push(match);
            return `{URL:${urls.length - 1}}`;
        });

        const mentionWrapped = urlsRemoved.replace(MentionRegex, (match, p1, p2, p3, p4) => {
            return formatOpts.includes('mention')
                // eslint-disable-next-line max-len
                ? `<span class="mention" data-index="0" data-denotation-char="@" data-value="${p1}" data-to-id="${p2}" data-from-id="${p3}" data-tag="${p4 || ''}">@${p1}</span>`
                : match;
        });

        const bothRegex = /___(.*?)___/g;
        const boldRegex = /__(.*?)__/g;
        const italicRegex = /_(.*?)_/g;

        const bothWrapped = mentionWrapped.replace(bothRegex, (match, p1) => {
            return formatOpts.includes('bold') && formatOpts.includes('italic')
                ? `<strong><em>${p1}</em></strong>`
                : match;
        });

        const boldWrapped = bothWrapped.replace(boldRegex, (match, p1) => {
            return formatOpts.includes('bold')
                ? `<strong>${p1}</strong>`
                : match;
        });

        const italicWrapped = boldWrapped.replace(italicRegex, (match, p1) => {
            return formatOpts.includes('italic')
                ? `<em>${p1}</em>`
                : match;
        });

        const urlsReturned = italicWrapped.replace(/{URL:(\d+)}/g, (match, p1) => {
            return urls[p1];
        });

        const linkWrapped = urlsReturned.replace(UrlRegex, (match) => {
            return formatOpts.includes('link')
                ? `<a href="${match}" target="_blank" rel="noopener noreferrer">${match}</a>`
                : match;
        });

        return linkWrapped;
    },

    parseMentions(savedForm: string) {

        const copy = new RegExp(MentionRegex);
        return Array.from(savedForm.matchAll(copy))
            .filter(x => !!x)
            .map(m => {
                const [all, value, toId, fromId, tag] = m;
                return { value, toId, fromId, tag, index: m.index, length: all.length };
            });
    },

    toExportableForm(savedForm: string) {
        // Simplify the mention syntax for export
        return savedForm.replace(MentionRegex, (match, p1, _p2, _p3, _p4) => {
            return `[@${p1}]`;
        });
    },

};
